<script lang="ts" setup>
import { ref, PropType, watchEffect, onMounted, onBeforeUnmount } from 'vue';
import {
  ComputeTaskUtils,
  DataPipelineTaskTabOptionType,
  DATA_PIPELINE_TASK_TAB_OPTIONS,
} from '@/utils/compute-utils';
import { ComputeTaskType } from '@/gql/graphql';
import {
  BBadge,
  BButton,
  BButtonGroup,
  BCard,
  BCollapse,
  BTable,
} from 'bootstrap-vue-next';
import { useUserStore } from '@/store/user';
import { IS_DEBUG_MODE, COMPUTE_TASK_STATUS } from '@/constants';
import ComputeTaskFilesList from '@/components/compute/ComputeTaskFilesList.vue';
import ComputeTaskStatusBadge from '@/components/compute/ComputeTaskStatusBadge.vue';

const userStore = useUserStore();

// --- Component Properties ---
const props = defineProps({
  task: { type: Object as PropType<ComputeTaskType> },
  expanded: { type: Boolean, default: false },
  selectedTab: {
    type: String as PropType<DataPipelineTaskTabOptionType>,
    default: DATA_PIPELINE_TASK_TAB_OPTIONS.FILES,
  },
});

// --- Emits ---
const emits = defineEmits<{
  (e: 'update:expanded', val: boolean): void;
  (e: 'update:selected-tab', val: DataPipelineTaskTabOptionType): void;
  (e: 'bucket-requested'): void;
  (e: 'exec-details-requested'): void;
  (e: 'debug-details-requested'): void;
  (e: 'abort-requested'): void;
  (e: 'file-preview-requested', file: TaskFileType): void;
  (e: 'file-download-requested', file: TaskFileType): void;
  (
    e: 'layout-needed',
    phase: 'start' | 'end' | 'resize',
    id?: number | null
  ): void;
}>();

// helper to emit layout events with the task id
function emitLayout(phase: 'start' | 'end' | 'resize') {
  emits('layout-needed', phase, props.task?.id ?? null);
}

// Observe size changes (images/tables loading) and notify parent
const cardRef = ref<InstanceType<typeof BCard> | null>(null);
let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  const el = cardRef.value?.$el as HTMLElement | undefined;
  if (!el || typeof ResizeObserver === 'undefined') return;
  resizeObserver = new ResizeObserver(() => emitLayout('resize'));
  resizeObserver.observe(el);
});

onBeforeUnmount(() => resizeObserver?.disconnect());

// local reactive state ― decoupled from prop so the card can toggle itself
const selectedTab = ref(props.selectedTab);
const isExpanded = ref(props.expanded);
const detailedView = ref(true);

watchEffect(
  () => (
    (isExpanded.value = props.expanded),
    console.debug('isExpanded set to', isExpanded.value, 'from prop')
  )
);
watchEffect(
  () => (
    (selectedTab.value = props.selectedTab),
    console.debug('selectedTab set to', selectedTab.value, 'from prop')
  )
);

function selectTab(tab: DataPipelineTaskTabOptionType) {
  selectedTab.value = tab;
  emits('update:selected-tab', selectedTab.value);
}

function toggleCard() {
  isExpanded.value = !isExpanded.value;
  emits('update:expanded', isExpanded.value);
}

function expandCard() {
  if (!isExpanded.value) {
    toggleCard();
  }
}

// --- Files Output ---
export type TaskFileType = {
  name: string;
  path: string;
  type: string;
  fileType: string;
  description: string;
};

// --- Sections for Sample Report ---
type ProgressRow = {
  label: string;
  value: unknown;
  format?: Intl.NumberFormatOptions; // optional override
  scale?: number; // multiply before formatting (e.g., 100 for percentage)
  suffix?: string; // e.g., ' %'
};

type ProgressSection = {
  key?: string;
  title: string;
  items: ProgressRow[];
  state?: string; // COMPUTE_TASK_STATUS
};

function progressSectionState(
  status?: string
): 'pending' | 'active' | 'error' | 'done' {
  // initial validation of task and its status
  if (
    !status ||
    !props.task ||
    !props.task.status ||
    status === COMPUTE_TASK_STATUS.PENDING
  ) {
    return 'pending';
  }
  // running section is active if the task is running
  if (
    status === COMPUTE_TASK_STATUS.RUNNING ||
    status === COMPUTE_TASK_STATUS.CREATING
  ) {
    if (props.task.status === COMPUTE_TASK_STATUS.RUNNING) {
      return 'active';
    }
    return 'error';
  }
  // completed section is done if the task is completed
  if (status === COMPUTE_TASK_STATUS.COMPLETED) {
    return 'done';
  }
  // otherwise, the section is failed
  return 'error';
}

function taskProgress(sample: any): ProgressSection[] {
  const p = sample?.report?.progress || {};

  // helper to keep only defined values
  const rows = (entries: ProgressRow[]) =>
    entries.filter((e) => e.value !== undefined && e.value !== null);

  return [
    {
      key: 'transfer',
      title: 'Data',
      items: rows([
        { label: 'Reads total', value: p.transfer_data?.raw_total },
      ]),
      state: progressSectionState(p.transfer_data?.phase_status),
    },
    {
      key: 'extract',
      title: 'Extracted Barcodes',
      items: rows([
        {
          label: 'Extracted total',
          value: p.extract_barcodes?.extracted_total,
        },
        {
          label: 'Loss',
          value: p.extract_barcodes?.loss_raw_to_extracted,
        },
        {
          label: 'Extracted percentage',
          value: p.extract_barcodes?.extracted_fraction,
          format: { maximumFractionDigits: 1 },
          scale: 100,
          suffix: ' %',
        },
      ]),
      state: progressSectionState(p.extract_barcodes?.phase_status),
    },
    {
      key: 'trim',
      title: 'Trimming',
      items: rows([
        {
          label: 'Trimmed 5` total',
          value: p.trimming?.trimmed_5prime_total,
        },
        {
          label: 'Loss extracted to trimmed',
          value: p.trimming?.loss_extracted_to_trimmed,
        },
        {
          label: 'Trimmed percentage',
          value: p.trimming?.trimmed_fraction,
          format: { maximumFractionDigits: 1 },
          scale: 100,
          suffix: ' %',
        },
      ]),
      state: progressSectionState(p.trimming?.phase_status),
    },
    {
      key: 'map_human',
      title: 'Mapping on Human Genome',
      items: rows([
        { label: 'Mapped total', value: p.mapping_human?.mapped_total },
        {
          label: 'Loss',
          value: p.mapping_human?.loss_trimmed_to_mapped,
        },
        {
          label: 'Mapped percentage',
          value: p.mapping_human?.mapped_total_fraction,
          format: { maximumFractionDigits: 1 },
          scale: 100,
          suffix: ' %',
        },
      ]),
      state: progressSectionState(p.mapping_human?.phase_status),
    },
    {
      key: 'count_human',
      title: 'Counts (Human)',
      items: rows([
        {
          label: 'Assigned total',
          value: p.counts_human?.counts_assigned,
        },
        {
          label: 'Loss mapped to counts',
          value: p.counts_human?.loss_mapped_to_counts,
        },
        {
          label: 'Assigned percentage',
          value: p.counts_human?.counts_assigned_fraction,
          format: { maximumFractionDigits: 1 },
          scale: 100,
          suffix: ' %',
        },
        {
          label: 'Hits total',
          value: p.counts_human?.hits_total,
        },
        {
          label: 'Loss counts to hits',
          value: p.counts_human?.loss_counts_to_hits,
        },
        {
          label: 'Hits percentage',
          value: p.counts_human?.hits_fraction,
          format: { maximumFractionDigits: 1 },
          scale: 100,
          suffix: ' %',
        },
      ]),
      state: progressSectionState(p.counts_human?.phase_status),
    },
    {
      key: 'filter_out_fakes_human',
      title: 'Filtered fakes (Human)',
      items: rows([
        {
          label: 'Filter hits total',
          value:
            p.filter_out_fakes_human?.umi_tools_hits_log
              ?.fake_filtered_out_hits_total,
        },
        {
          label: 'Filter hits percentage',
          value:
            p.filter_out_fakes_human?.umi_tools_hits_log
              ?.fake_filtered_out_hits_fraction,
          format: { maximumFractionDigits: 1 },
          scale: 100,
          suffix: ' %',
        },
        {
          label: 'Threshold barcodes total',
          value:
            p.filter_out_fakes_human?.hits_to_ann_data_threshold
              ?.barcodes_total,
        },
        {
          label: 'Threshold barcodes with 50+ UMIs',
          value:
            p.filter_out_fakes_human?.hits_to_ann_data_threshold?.barcodes_kept,
        },
        {
          label: 'Threshold UMIs yield from 50+ UMIs barcode',
          value:
            p.filter_out_fakes_human?.hits_to_ann_data_threshold
              ?.umi_yield_pct_threshold,
        },
      ]),
      state: progressSectionState(p.filter_out_fakes_human?.phase_status),
    },
    {
      key: 'mapping_bioparticles',
      title: 'Mapping on Bioparticle Genome',
      items: rows([
        {
          label: 'Mapped total',
          value: p.mapping_bioparticles?.mapped_total,
        },
        {
          label: 'Loss',
          value: p.mapping_bioparticles?.loss_trimmed_to_mapped,
        },
        {
          label: 'Mapped percentage',
          value: p.mapping_bioparticles?.mapped_total_fraction,
          format: { maximumFractionDigits: 1 },
          scale: 100,
          suffix: ' %',
        },
      ]),
      state: progressSectionState(p.mapping_bioparticles?.phase_status),
    },
    {
      key: 'counts_bioparticles',
      title: 'Counts (Bioparticles)',
      items: rows([
        {
          label: 'Assigned total',
          value: p.counts_bioparticles?.counts_assigned,
        },
        {
          label: 'Loss mapped to counts',
          value: p.counts_bioparticles?.loss_mapped_to_counts,
        },
        {
          label: 'Assigned percentage',
          value: p.counts_bioparticles?.counts_assigned_fraction,
          format: { maximumFractionDigits: 1 },
          scale: 100,
          suffix: ' %',
        },
        {
          label: 'Hits total',
          value: p.counts_bioparticles?.hits_total,
        },
        {
          label: 'Loss counts to hits',
          value: p.counts_bioparticles?.loss_counts_to_hits,
        },
        {
          label: 'Hits percentage',
          value: p.counts_bioparticles?.hits_fraction,
          format: { maximumFractionDigits: 1 },
          scale: 100,
          suffix: ' %',
        },
      ]),
      state: progressSectionState(p.counts_bioparticles?.phase_status),
    },
  ];
}

function formatProgressRow(
  row: ProgressRow,
  locale = navigator.language || 'en-US'
): string {
  const val =
    typeof row.value === 'number'
      ? row.value
      : Number.isFinite(Number(row.value))
      ? Number(row.value)
      : (row.value as any) ?? '';

  // default scale and suffix
  const scale = row.scale ?? 1;
  const suffix = row.suffix ?? '';

  // non-numeric → show raw
  if (typeof val !== 'number') {
    return `${val}${suffix}`;
  }

  // numeric → show formatted
  const text = new Intl.NumberFormat(locale, row.format).format(val * scale);
  return `${text}${suffix}`;
}

const progressFields = [
  { key: 'label', label: 'Metric' },
  { key: 'value', label: 'Value', tdClass: 'text-end font-monospace' },
];
</script>
<template>
  <div class="col" v-if="props.task?.id">
    <BCard
      ref="cardRef"
      :class="[
        'd-flex flex-column p-0 card-sample',
        isExpanded ? 'expanded' : 'collapsed h-100',
      ]"
    >
      <slot>
        <!-- Task Header: Tile+Subtitle | ActionButtons -->
        <div
          class="d-flex flex-column flex-sm-row align-items-start justify-content-between gap-2"
        >
          <!-- Task Header Left: Title+Subtitle -->
          <div class="me-sm-3 flex-grow-1">
            <!-- Task Name -->
            <h2
              @click="toggleCard"
              :id="`task-title-${props.task.id}`"
              class="mb-0 task-title"
              v-b-tooltip.hover
              :title="isExpanded ? 'Click to collapse' : 'Click to expand'"
            >
              <ComputeTaskStatusBadge
                v-if="!isExpanded"
                :task="props.task"
                icon-only
              />
              <!-- Task Name-->
              {{ props.task.name }}
              <BBadge variant="secondary" class="ms-2">
                <small style="font-weight: lighter">
                  <i class="fas fa-archive"></i>
                  Legacy
                </small>
              </BBadge>
              <!-- Collapse icon -->
              <font-awesome-icon
                v-if="isExpanded"
                icon="chevron-up"
                class="ms-2"
              />
            </h2>
            <!-- Progress Text Description / Error Message -->
            <div class="my-2">
              <ComputeTaskStatusBadge :task="props.task" />
            </div>
          </div>
          <!-- Task Header Right: Show More / Action Buttons -->
          <div class="mb-2 ms-sm-auto">
            <b-button
              @click="expandCard"
              v-if="!isExpanded"
              variant="link"
              size="sm"
              class="p-0 m-0 link-secondary text-decoration-none"
            >
              Show More
              <font-awesome-icon icon="chevron-down" class="ms-1" />
            </b-button>
            <!-- Action buttons -->
            <b-button-group v-if="isExpanded">
              <!-- Progress Timeline Link -->
              <b-button
                v-if="props.task.bucket"
                size="sm"
                variant="outline-dark"
                @click="
                  expandCard();
                  selectTab(DATA_PIPELINE_TASK_TAB_OPTIONS.PROGRESS);
                "
                :id="`btn-progress-${props.task.id}`"
                v-b-tooltip.hover
                title="View progress timeline with stats"
                :active="
                  isExpanded &&
                  selectedTab === DATA_PIPELINE_TASK_TAB_OPTIONS.PROGRESS
                "
              >
                <font-awesome-icon icon="list-check" />
              </b-button>
              <!-- Figures -->
              <b-button
                hidden
                v-if="props.task.bucket"
                size="sm"
                variant="outline-dark"
                @click="
                  expandCard();
                  selectTab(DATA_PIPELINE_TASK_TAB_OPTIONS.FIGURES);
                "
                :id="`btn-figures-${props.task.id}`"
                v-b-tooltip.hover
                title="View figures"
                :active="
                  isExpanded &&
                  selectedTab === DATA_PIPELINE_TASK_TAB_OPTIONS.FIGURES
                "
              >
                <font-awesome-icon icon="chart-line" />
              </b-button>
              <!-- Files List -->
              <b-button
                v-if="props.task.bucket"
                size="sm"
                variant="outline-dark"
                @click="
                  expandCard();
                  selectTab(DATA_PIPELINE_TASK_TAB_OPTIONS.FILES);
                "
                :id="`btn-files-${props.task.id}`"
                v-b-tooltip.hover
                title="View files"
                :active="
                  isExpanded &&
                  selectedTab === DATA_PIPELINE_TASK_TAB_OPTIONS.FILES
                "
              >
                <font-awesome-icon icon="file" />
              </b-button>
              <!-- Exec Configuration -->
              <BButton
                variant="outline-dark"
                size="sm"
                @click="emits('exec-details-requested')"
                :id="`btn-exec-${props.task.id}`"
                v-b-tooltip.hover
                title="View exec configuration"
              >
                <font-awesome-icon :icon="['fas', 'sliders']" />
              </BButton>
              <!-- Bucket Link for Developers -->
              <BButton
                v-if="IS_DEBUG_MODE || userStore.developer"
                variant="outline-dark"
                size="sm"
                @click="emits('bucket-requested')"
                :id="`btn-bucket-${props.task.id}`"
                :disabled="!props.task.bucket"
                v-b-tooltip.hover
                title="Go to data"
              >
                <font-awesome-icon :icon="['fas', 'database']" />
              </BButton>
              <!-- Debug Details -->
              <BButton
                v-if="IS_DEBUG_MODE || userStore.developer"
                variant="outline-dark"
                size="sm"
                @click="emits('debug-details-requested')"
                :id="`btn-debug-${props.task.id}`"
                v-b-tooltip.hover
                title="View debug details"
              >
                <font-awesome-icon :icon="['fas', 'code']" />
              </BButton>
              <!-- Abort Compute Task -->
              <b-button
                size="sm"
                variant="outline-danger"
                :disabled="!ComputeTaskUtils.canBeAborted(props.task.status)"
                @click="emits('abort-requested')"
                :id="`btn-abort-${props.task.id}`"
                v-b-tooltip.hover
                title="Abort task"
                :active="props.task.status === COMPUTE_TASK_STATUS.ABORTING"
              >
                <font-awesome-icon icon="times" />
              </b-button>
            </b-button-group>
          </div>
          <!-- end of action buttons group div -->
        </div>
        <!-- end of task header row -->

        <!-- Collapsible Task Content -->
        <BCollapse
          v-model="isExpanded"
          class="mt-2 flex-grow-1"
          @show="emitLayout('start')"
          @hide="emitLayout('start')"
          @shown="emitLayout('end')"
          @hidden="emitLayout('end')"
        >
          <!-- Sample Detail -->
          <div
            v-for="(sample, sid) in props.task.results?.samples || []"
            :key="sid"
          >
            <!-- sample name -->
            <h3
              v-if="sample.name && props.task.results?.samples.length > 1"
              class="mb-2"
            >
              Sample {{ sample.name }}
            </h3>

            <!-- Sample Progress Timeline -->
            <template
              v-if="
                sample.report.progress &&
                selectedTab === DATA_PIPELINE_TASK_TAB_OPTIONS.PROGRESS
              "
            >
              <h3
                @click="detailedView = !detailedView"
                style="cursor: pointer"
                :title="detailedView ? 'Click to collapse' : 'Click to expand'"
                :id="`progress-title-${props.task.id}`"
                v-b-tooltip.hover
              >
                Progress
                <i
                  :class="
                    detailedView
                      ? ['ms-1', 'bi', 'bi-arrows-collapse']
                      : ['ms-1', 'bi', 'bi-arrows-expand']
                  "
                />
              </h3>
              <div class="route">
                <div
                  v-for="(sec, i) in taskProgress(sample)"
                  :key="sec.key || i"
                  class="route__section"
                >
                  <!-- circular point -->
                  <div class="route__marker" :class="sec.state">
                    <span class="visually-hidden"
                      >ProgressSection {{ i + 1 }}</span
                    >
                  </div>

                  <!-- right side body -->
                  <div class="route__body">
                    <h5 class="route__title mb-1">{{ sec.title }}</h5>

                    <b-collapse
                      v-model="detailedView"
                      @show="emitLayout('start')"
                      @hide="emitLayout('start')"
                      @shown="emitLayout('end')"
                      @hidden="emitLayout('end')"
                    >
                      <div v-if="sec.items?.length" class="table-border mb-1">
                        <BTable
                          :items="sec.items"
                          :fields="progressFields"
                          small
                          striped
                          hover
                          responsive="sm"
                          stacked="sm"
                          class="browser-table route__table mb-0"
                          thead-class="visually-hidden"
                        >
                          <!-- show the raw label -->
                          <template #cell(label)="{ item }">
                            {{ item.label }}
                          </template>

                          <!-- format numeric values per your locale/row rules -->
                          <template #cell(value)="{ item }">
                            {{ formatProgressRow(item) }}
                          </template>
                        </BTable>
                      </div>
                    </b-collapse>
                  </div>
                </div>
              </div>
              <!-- End of Timeline Div -->
            </template>
            <!-- End of Progress Timeline Template -->

            <!-- List Figures -->
            <div v-if="selectedTab === DATA_PIPELINE_TASK_TAB_OPTIONS.FIGURES">
              <h3>Figures</h3>
              <p>Figure details go here...</p>
            </div>
            <!-- End of Figures Div -->

            <!-- List Files -->
            <div v-if="selectedTab === DATA_PIPELINE_TASK_TAB_OPTIONS.FILES">
              <ComputeTaskFilesList
                :files="sample.report.files"
                @file-preview-requested="
                  emits('file-preview-requested', $event)
                "
                @file-download-requested="
                  emits('file-download-requested', $event)
                "
              />
            </div>
            <!-- End of Files Div -->
          </div>
          <!-- End of Sample Div -->
        </BCollapse>
        <!-- End of Collapsible Task Content -->
      </slot>
    </BCard>
    <!-- End of Task Card-->
  </div>
</template>
<style scoped>
div.table-border {
  padding: 5px;
  border-radius: 5px;
  background-color: #fff;
  border: 1px solid #dee2e6;
}
.card-sample {
  display: flex;
  background-color: #f5f5f5;
  overflow: visible;

  h2 {
    font-size: 1rem;
    font-weight: bold;
  }

  h2.task-title {
    cursor: pointer;
  }

  h3 {
    font-size: 0.8rem;
    font-weight: bold;
  }

  h5 {
    font-size: 0.8rem;
    font-weight: bold;
  }
}

/* --- Route Timeline --- */
.route {
  --route-color-active: var(--bs-primary);
  --route-color-pending: var(--bs-secondary);
  --route-color-error: var(--bs-danger);
  --route-color-done: var(--bs-success);

  --route-color-line: var(--bs-border-color, #d0d7de);
  --route-color-dot: #fff;

  --route-gap: 0.25rem;
  --route-dot-size: 14px;
  --route-line-width: 2px;
  --route-marker-nudge: 1px; /* + moves dot down, - moves dot up */
}

/* Contain the vertical guide line and any absolutely-positioned dots */
.route {
  position: relative;
}

/* If you draw the vertical line with ::before, anchor it to the card, not a fragment */
.route::before {
  top: 0;
  bottom: 0;
}

/* one section row: two columns -> [marker] [content] */
.route__section {
  position: relative;
  display: grid;
  grid-template-columns: calc(var(--route-dot-size) + 6px) 1fr;
  align-items: start;
  column-gap: var(--route-gap);
}

/* vertical dashed connector (segment below the current dot) */
.route__section::after {
  content: '';
  position: absolute;
  left: calc((var(--route-dot-size) + 6px) / 2 - var(--route-line-width) / 2);
  top: calc(var(--route-dot-size) + 4px + var(--route-marker-nudge));
  bottom: 0;
  border-left: var(--route-line-width) dashed var(--route-color-line);
}
.route__section:last-child::after {
  display: none;
} /* no line after last dot */

/* the circular dot */
.route__marker {
  position: relative;
  width: calc(var(--route-dot-size) + 6px);
  height: var(--route-dot-size);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: var(--route-marker-nudge);
}
.route__marker::before {
  content: '';
  width: var(--route-dot-size);
  height: var(--route-dot-size);
  border-radius: 50%;
  background: var(--route-color-dot);
  box-shadow: 0 0 0 3px var(--route-color-line) inset;
}

/* Heartbeat + ripple when in progress */
.route__marker.active {
  position: relative;
  overflow: visible; /* let ripple grow outside the cell */
  will-change: transform;
}

/* optional states (colors of the ring) */
.route__marker.pending::before {
  box-shadow: 0 0 0 3px var(--route-color-line) inset;
}
.route__marker.active::before {
  box-shadow: 0 0 0 3px var(--route-color-active) inset;
  /* background: var(--route-color-active); */
  /* the dot: grow/shrink */
  animation: route-beat 1.1s ease-in-out infinite;
  transform-origin: center;
}
.route__marker.done::before {
  box-shadow: 0 0 0 3px var(--route-color-done) inset;
  /* background: var(--route-color-done); */
}
.route__marker.error::before {
  box-shadow: 0 0 0 3px var(--route-color-error) inset;
}

/* the ripple ring */
.route__marker.active::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: var(--route-dot-size);
  height: var(--route-dot-size);
  transform-origin: center;
  border-radius: 50%;
  border: 1px solid var(--route-color-active); /* animated ring color */
  opacity: 0.35;
  transform: scale(1);
  animation: route-ripple 1.1s ease-out infinite;
  pointer-events: none;
}

/* route title */
.route__title {
  line-height: 1.3;
}

/* definition list rows */
.route__stats {
  margin: 0;
}
.route__row {
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
}
.route__row dt {
  font-weight: 600;
}
.route__row dd {
  margin: 0;
}

/* keyframes */
@keyframes route-beat {
  0%,
  100% {
    transform: scale(1);
  }
  30% {
    transform: scale(1.15);
  }
  60% {
    transform: scale(1.05);
  }
}

@keyframes route-ripple {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.35;
  }
  70% {
    transform: translate(-50%, -50%) scale(1.9);
    opacity: 0;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.9);
    opacity: 0;
  }
}

/* Respect reduced-motion user preference */
@media (prefers-reduced-motion: reduce) {
  .route__marker.inprogress::before,
  .route__marker.inprogress::after {
    animation: none !important;
  }
}
</style>
